/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import currency from "currency.js";
import {
  GetAccount200ResponsePositionsInner,
  MarginTypeResponse,
  SideResponse,
} from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { apiNum } from "../../utils/math";
import {
  formatKnownPrice,
  formatKnownValue,
  formatRoiPercent,
  signedTone,
} from "../../utils/unknownNumbers";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../utils/format";
import { getDisplayInstrumentName } from "../../utils/displayName";
import { RowStatusCaption } from "../PortfolioSettings/Table/style";
import { getMarginTypeLabel, normalizeMarginType } from "../../utils/margin";
import { getPositionIdentity } from "../../utils/positionIdentity";
import { IModifiedPosition } from "./model";
import PositionCardTpslValue from "./PositionCardTpslValue";
import {
  PositionCard,
  PositionCardCloseBtn,
  PositionCardCoin,
  PositionCardField,
  PositionCardFooter,
  PositionCardGrid,
  PositionCardHeader,
  PositionCardLev,
  PositionCardList,
  PositionCardLoadMore,
  PositionCardPnl,
} from "./style";

interface IPositionCardsViewProps {
  modifiedPositions: IModifiedPosition[];
  accountData: any;
  t: any;
  onSelectPosition: (position: GetAccount200ResponsePositionsInner) => void;
  onClosePositionWithId: (instrument_id: string) => void;
  /** Opens the TP/SL modal. Omitted on chains without a trigger executor. */
  onEditTpsl?: (positionId: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

function PositionCardsView({
  modifiedPositions,
  accountData,
  t,
  onSelectPosition,
  onClosePositionWithId,
  onEditTpsl,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: IPositionCardsViewProps) {
  return (
    <PositionCardList>
      {modifiedPositions.map((pos) => {
        const isLong = pos.side === SideResponse.Buy;
        const lev =
          (pos as any).leverage ??
          accountData?.leverages?.find(
            (l) => l.instrument_id === pos.instrument_id
          )?.leverage;
        // `undefined` when unknown (market unresolved): shown as "-", never $0.
        const pnl = apiNum(pos.unrealized_pnl);
        const pnlColor = signedTone(pnl, {
          up: COLORS.up.one,
          down: COLORS.down.one,
          unknown: TEXT_COLORS.three,
        });
        const roi = apiNum(pos.roi);
        const marginType = normalizeMarginType(pos.margin_type);
        const margin =
          marginType === MarginTypeResponse.Isolated
            ? pos.isolated_margin
            : pos.maintenance_margin;
        const liq = Number((pos as any).liquidation_price);
        const pp = pos.pricePrecision;
        const id = getPositionIdentity(pos) || pos.instrument_id;
        return (
          <PositionCard
            key={id}
            $isLong={isLong}
            onClick={() =>
              onSelectPosition(pos as GetAccount200ResponsePositionsInner)
            }
          >
            <PositionCardHeader>
              <PositionCardCoin>
                {getDisplayInstrumentName(pos.instrument_name) ||
                  pos.instrument_name}
                {lev ? <PositionCardLev>{Number(lev)}x</PositionCardLev> : null}
                {pos.__tradeSyncSettling && (
                  // Same wording and style as the desktop table: a close is in
                  // flight that could be this row, but is not provably this row.
                  <RowStatusCaption>
                    {t("close_pending", { defaultValue: "Close pending" })}
                  </RowStatusCaption>
                )}
              </PositionCardCoin>
              <PositionCardPnl style={{ color: pnlColor }}>
                {formatCurrencyWithSubscript(pnl, {
                  showPositiveSign: true,
                })}
                {roi !== undefined ? (
                  <span>({formatRoiPercent(roi)})</span>
                ) : null}
              </PositionCardPnl>
            </PositionCardHeader>
            <PositionCardGrid>
              <PositionCardField>
                <span>{t("contracts")}</span>
                <span>
                  {formatSizePrecision(pos.amount, pos.amountPrecision)}
                </span>
              </PositionCardField>
              <PositionCardField>
                <span>{t("position_value")}</span>
                <span>{formatKnownValue(pos.positionValue)}</span>
              </PositionCardField>
              <PositionCardField>
                <span>{t("avg_entry_price")}</span>
                <span>
                  {currency(pos.avg_entry_price || 0, {
                    precision: pp,
                  }).format()}
                </span>
              </PositionCardField>
              <PositionCardField>
                <span>{t("mark_price")}</span>
                <span>
                  {formatKnownPrice(pos.mark_price, pp)}
                </span>
              </PositionCardField>
              <PositionCardField>
                <span>{t("liquidation_price")}</span>
                <span>
                  {liq > 0
                    ? currency(liq, { precision: pp }).format()
                    : "-"}
                </span>
              </PositionCardField>
              <PositionCardField>
                <span>{t("margin", { defaultValue: "Margin" })}</span>
                <span>
                  {formatCurrencyWithSubscript(apiNum(margin))} (
                  {getMarginTypeLabel(pos.margin_type)})
                </span>
              </PositionCardField>
            </PositionCardGrid>
            <PositionCardFooter>
              <PositionCardCloseBtn
                onClick={(e) => {
                  e.stopPropagation();
                  onClosePositionWithId(id);
                }}
              >
                {t("close", { defaultValue: "Close" })}
              </PositionCardCloseBtn>
              <PositionCardTpslValue
                position={pos as GetAccount200ResponsePositionsInner}
                positionId={id}
                pricePrecision={pp}
                onEdit={onEditTpsl}
              />
            </PositionCardFooter>
          </PositionCard>
        );
      })}
      {onLoadMore && hasMore && (
        <PositionCardLoadMore
          onClick={onLoadMore}
          disabled={!!isLoadingMore}
        >
          {isLoadingMore
            ? t("loading", { defaultValue: "Loading…" })
            : t("load_more", { defaultValue: "Load more" })}
        </PositionCardLoadMore>
      )}
    </PositionCardList>
  );
}

export default PositionCardsView;
